import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useSidebarCollapsed } from "@ncp/ui";

const SIDEBAR_KEY = "ncp.assistant.sidebar";
const FOCUS_SIDEBAR_KEY = "ncp.assistant.sidebar.focus";

interface ShellState {
  /** The space sidebar is folded away (the rail stays). */
  collapsed: boolean;
  toggleSidebar: () => void;
  /** A screen that wants the room (a work item) folds the sidebar until the officer pins it open there. */
  focus: boolean;
  setFocus: (on: boolean) => void;
  /** The drawer that holds the rail and the sidebar below `lg`. */
  navOpen: boolean;
  setNavOpen: (open: boolean) => void;
  /** An app shown without the rail, sidebar and top bar. */
  immersive: boolean;
  setImmersive: (on: boolean) => void;
  /** Opens the start dialog for a study or policy, optionally on one playbook. */
  startWork: (playbookKey?: string | null) => void;
  starting: { playbookKey: string | null } | null;
  stopStarting: () => void;
}

const ShellContext = createContext<ShellState | null>(null);

export function ShellStateProvider({ children }: { children: ReactNode }) {
  const [stored, toggleStored] = useSidebarCollapsed(SIDEBAR_KEY);
  const [focus, setFocus] = useState(false);
  const [focusOpen, setFocusOpen] = useState<boolean>(() => {
    try { return localStorage.getItem(FOCUS_SIDEBAR_KEY) === "open"; } catch { return false; }
  });
  const [navOpen, setNavOpen] = useState(false);
  const [immersive, setImmersive] = useState(false);
  const [starting, setStarting] = useState<{ playbookKey: string | null } | null>(null);

  const toggleSidebar = useCallback(() => {
    if (!focus) { toggleStored(); return; }
    setFocusOpen((open) => {
      try { localStorage.setItem(FOCUS_SIDEBAR_KEY, open ? "folded" : "open"); } catch { /* storage unavailable */ }
      return !open;
    });
  }, [focus, toggleStored]);

  useEffect(() => {
    if (!immersive) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setImmersive(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [immersive]);

  const value = useMemo<ShellState>(() => ({
    collapsed: focus ? !focusOpen : stored,
    toggleSidebar,
    focus,
    setFocus,
    navOpen,
    setNavOpen,
    immersive,
    setImmersive,
    startWork: (playbookKey) => setStarting({ playbookKey: playbookKey ?? null }),
    starting,
    stopStarting: () => setStarting(null),
  }), [focus, focusOpen, stored, toggleSidebar, navOpen, immersive, starting]);

  return <ShellContext.Provider value={value}>{children}</ShellContext.Provider>;
}

export function useShell(): ShellState {
  const value = useContext(ShellContext);
  if (!value) throw new Error("useShell must be used inside the workspace shell");
  return value;
}

/** Folds the space sidebar while the calling screen is shown (the officer can pin it open again). */
export function useFocusLayout(on = true): void {
  const { setFocus } = useShell();
  useEffect(() => {
    if (!on) return;
    setFocus(true);
    return () => setFocus(false);
  }, [on, setFocus]);
}
